import { apiClient, handleApiRequest } from './client'
import type { AuthResponse } from '@/types'

export interface SchoolDomain {
  id: string
  domain: string
  is_primary: boolean
  school_id: string
  created_at: string
}

export interface SchoolLanguage {
  id: string
  school_id: string
  language_code: string
  display_name: string | null
  is_primary: boolean
}

export interface CountryInfo {
  code: string
  name: string
  name_de: string | null
  name_en: string | null
  phone_prefix: string | null
  default_timezone: string | null
  is_eu: boolean
  is_active: boolean
}

export interface RegionInfo {
  id: string
  country_code: string
  code: string | null
  name: string
  public_holiday_region_code: string | null
  is_active: boolean
}

export interface School {
  id: string
  name: string
  short_name: string | null
  address: string | null
  city: string | null
  zip_code: string | null
  country: string
  region_id: string | null
  region_name?: string | null
  website: string | null
  phone: string | null
  email: string | null
  logo_url: string | null
  language: string | null
  is_active: boolean
  created_at: string
  updated_at: string
  domains: SchoolDomain[]
  country_info: CountryInfo | null
  region: RegionInfo | null
  languages: SchoolLanguage[]
  partner_company_id?: string | null
  partner_company_name?: string | null
  staff_card_hint?: string | null
  billing_currency?: string | null
  billing_deal_percent?: number | string | null
  timezone?: string | null
}

export type UaSchool = School

export interface SuperadminInfo {
  id: string
  email: string
  first_name: string | null
  last_name: string | null
  school_id: string
  school_name: string
  is_active: boolean
  created_at: string
}

export interface SuperadminCreateRequest {
  email: string
  first_name: string
  last_name: string
  school_id: string
  phone?: string
  teacher_code?: string
}

export interface SchoolLanguageCreate {
  language_code: string
  display_name?: string
  is_primary: boolean
}

export interface SchoolCreateRequest {
  name: string
  short_name: string
  address?: string
  city?: string
  zip_code?: string
  country?: string
  region_id?: string
  language?: string
  languages?: SchoolLanguageCreate[]
  email?: string
  website?: string
  phone?: string
  logo_url?: string
  is_active?: boolean
  partner_company_id?: string | null
  billing_currency?: string
  timezone?: string
}

export interface DomainCreateRequest {
  domain: string
  is_primary: boolean
}

export interface SuperadminCreateResponse {
  message: string
  user: SuperadminInfo
}

export interface DashboardStatsResponse {
  total_schools: number
  total_users: number
  substitutions_filled_total: number
  substitutions_filled_this_month: number
  substitutions_filled_today: number
  schools_by_country: Array<{ country: string; count: number }>
  overdue_school_count?: number
}

export interface SuperadminCreateSimpleRequest {
  email: string
  first_name: string
  last_name: string
  phone?: string
}

export interface SuperadminTransferRequest {
  target_user_id: string
}

export interface SuperadminTransferResponse {
  message: string
  source_user: SuperadminInfo
  target_user: SuperadminInfo
}

export interface SchoolUser {
  id: string
  email: string
  first_name: string | null
  last_name: string | null
  role: string
  is_active: boolean
}

export interface SchoolStatsItem {
  id: string
  name: string
  short_name: string | null
  country: string
  region_id: string | null
  region_name: string | null
  city: string | null
  zip_code: string | null
  user_count: number
  is_active: boolean
  primary_domain: string | null
}

export interface SchoolsStatsResponse {
  schools: SchoolStatsItem[]
  total: number
  page: number
  page_size: number
}

export interface RegionFilterOption {
  id: string
  code: string | null
  name: string
  country_code: string
  country_name: string
}

export interface SchoolFilterOptions {
  countries: string[]
  regions: RegionFilterOption[]
  cities: string[]
  zip_codes: string[]
}

export const ultraadminApi = {
  listSchools: (params?: {
    is_active?: boolean
    search?: string
    country?: string
    language?: string
    currency?: string
    partner_company_id?: string
    direct?: boolean
    limit?: number
  }) => {
    const qs = new URLSearchParams()
    if (params?.is_active !== undefined) qs.set('is_active', String(params.is_active))
    if (params?.search) qs.set('search', params.search)
    if (params?.country) qs.set('country', params.country)
    if (params?.language) qs.set('language', params.language)
    if (params?.currency) qs.set('currency', params.currency)
    if (params?.partner_company_id) qs.set('partner_company_id', params.partner_company_id)
    if (params?.direct !== undefined) qs.set('direct', String(params.direct))
    if (params?.limit !== undefined) qs.set('limit', String(params.limit))
    const query = qs.toString() ? `?${qs.toString()}` : ''
    return handleApiRequest<UaSchool[]>(apiClient.get(`/api/v1/ultraadmin/schools${query}`))
  },

  createSchool: (data: SchoolCreateRequest) =>
    handleApiRequest<School>(apiClient.post('/api/v1/ultraadmin/schools', data)),

  updateSchool: (id: string, data: Partial<SchoolCreateRequest> & {
    bot_webhook_url?: string | null
    bot_webhook_auth?: string | null
    partner_company_id?: string | null
    region_id?: string | null
    address?: string | null
    city?: string | null
    zip_code?: string | null
    email?: string | null
    website?: string | null
    phone?: string | null
    logo_url?: string | null
  }) =>
    handleApiRequest<UaSchool>(apiClient.patch(`/api/v1/ultraadmin/schools/${id}`, data)),

  getTalk: () =>
    handleApiRequest<{
      system_prompt: string | null
      voice_map: Record<string, string>
      limit_turns: number
      limit_stt_s: number
      limit_tts_chars: number
      active_model: string | null
      rollback_banner: string | null
    }>(apiClient.get('/api/v1/ultraadmin/talk')),

  putTalk: (data: Record<string, unknown>) =>
    handleApiRequest(apiClient.put('/api/v1/ultraadmin/talk', data)),

  deactivateSchool: (id: string) =>
    handleApiRequest(apiClient.post(`/api/v1/ultraadmin/schools/${id}/deactivate`)),

  addDomain: (schoolId: string, data: DomainCreateRequest) =>
    handleApiRequest<SchoolDomain>(apiClient.post(`/api/v1/ultraadmin/schools/${schoolId}/domains`, data)),

  removeDomain: (schoolId: string, domainId: string) =>
    handleApiRequest(apiClient.delete(`/api/v1/ultraadmin/schools/${schoolId}/domains/${domainId}`)),

  listSuperadmins: (schoolId?: string) => {
    const query = schoolId ? `?school_id=${schoolId}` : ''
    return handleApiRequest<SuperadminInfo[]>(apiClient.get(`/api/v1/ultraadmin/superadmins${query}`))
  },

  createSuperadmin: (data: SuperadminCreateRequest) =>
    handleApiRequest<SuperadminCreateResponse>(apiClient.post('/api/v1/ultraadmin/superadmins', data)),

  getDashboardStats: () =>
    handleApiRequest<DashboardStatsResponse>(apiClient.get('/api/v1/ultraadmin/dashboard/stats')),

  createSuperadminForSchool: (schoolId: string, data: SuperadminCreateSimpleRequest) =>
    handleApiRequest<SuperadminCreateResponse>(apiClient.post(`/api/v1/ultraadmin/schools/${schoolId}/superadmin`, data)),

  deleteSuperadmin: (userId: string) =>
    handleApiRequest<{ message: string }>(apiClient.delete(`/api/v1/ultraadmin/superadmins/${userId}`)),

  activateSuperadmin: (userId: string) =>
    handleApiRequest<{ message: string }>(apiClient.post(`/api/v1/ultraadmin/superadmins/${userId}/activate`)),

  transferSuperadmin: (userId: string, data: SuperadminTransferRequest) =>
    handleApiRequest<SuperadminTransferResponse>(apiClient.post(`/api/v1/ultraadmin/superadmins/${userId}/transfer`, data)),

  getSchoolDomains: (schoolId: string) =>
    handleApiRequest<SchoolDomain[]>(apiClient.get(`/api/v1/ultraadmin/schools/${schoolId}/domains`)),

  getSchoolUsers: (schoolId: string, params?: { exclude_role?: string; is_active?: boolean }) => {
    const qs = new URLSearchParams()
    if (params?.exclude_role) qs.set('exclude_role', params.exclude_role)
    if (params?.is_active !== undefined) qs.set('is_active', String(params.is_active))
    const query = qs.toString() ? `?${qs.toString()}` : ''
    return handleApiRequest<{ users: SchoolUser[] }>(apiClient.get(`/api/v1/ultraadmin/schools/${schoolId}/users${query}`))
  },

  getSchoolsWithStats: (params?: {
    sort_by?: string; sort_order?: string; filter_name?: string;
    filter_country?: string; filter_city?: string; filter_zip_code?: string;
    skip?: number; limit?: number;
  }) => {
    const qs = new URLSearchParams()
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') qs.set(k, String(v))
      })
    }
    const query = qs.toString() ? `?${qs.toString()}` : ''
    return handleApiRequest<SchoolsStatsResponse>(apiClient.get(`/api/v1/ultraadmin/schools-stats${query}`))
  },

  getSchoolFilterOptions: () =>
    handleApiRequest<SchoolFilterOptions>(apiClient.get('/api/v1/ultraadmin/schools-stats/filters')),

  getCountries: () =>
    handleApiRequest<CountryInfo[]>(apiClient.get('/api/v1/ultraadmin/countries')),

  getCountryRegions: (countryCode: string) =>
    handleApiRequest<RegionInfo[]>(apiClient.get(`/api/v1/ultraadmin/countries/${countryCode}/regions`)),

  listActAsTeachers: (schoolId: string, q?: string) => {
    const qs = new URLSearchParams()
    qs.set('school_id', schoolId)
    if (q) qs.set('q', q)
    return handleApiRequest<{ teachers: ActAsTeacher[] }>(
      apiClient.get(`/api/v1/ultraadmin/act-as/teachers?${qs.toString()}`),
    )
  },

  listActAsSchools: (q?: string) => {
    const qs = new URLSearchParams()
    if (q) qs.set('q', q)
    return handleApiRequest<{ schools: ActAsSchool[] }>(
      apiClient.get(`/api/v1/ultraadmin/act-as/schools?${qs.toString()}`),
    )
  },

  listActAsPeople: (schoolId: string, q?: string) => {
    const qs = new URLSearchParams()
    qs.set('school_id', schoolId)
    if (q) qs.set('q', q)
    return handleApiRequest<{ people: ActAsPerson[] }>(
      apiClient.get(`/api/v1/ultraadmin/act-as/people?${qs.toString()}`),
    )
  },

  listActAsPartners: (q?: string) => {
    const qs = new URLSearchParams()
    if (q) qs.set('q', q)
    return handleApiRequest<{ partners: ActAsPartner[] }>(
      apiClient.get(`/api/v1/ultraadmin/act-as/partners?${qs.toString()}`),
    )
  },

  listActAsPartnerPeople: (companyId: string, q?: string) => {
    const qs = new URLSearchParams()
    qs.set('company_id', companyId)
    if (q) qs.set('q', q)
    return handleApiRequest<{ people: ActAsPerson[] }>(
      apiClient.get(`/api/v1/ultraadmin/act-as/partner-people?${qs.toString()}`),
    )
  },

  listActAsUltraadmins: (q?: string) => {
    const qs = new URLSearchParams()
    if (q) qs.set('q', q)
    return handleApiRequest<{ ultraadmins: ActAsUltraadmin[] }>(
      apiClient.get(`/api/v1/ultraadmin/act-as/ultraadmins?${qs.toString()}`),
    )
  },

  actAsTeacher: (teacherId: string) =>
    handleApiRequest<AuthResponse>(apiClient.post('/api/v1/ultraadmin/act-as/teacher', { teacher_id: teacherId })),

  startActAs: (body: ActAsStartBody) =>
    handleApiRequest<AuthResponse>(apiClient.post('/api/v1/ultraadmin/act-as/start', body)),

  switchActAsRole: (role: string) =>
    handleApiRequest<AuthResponse>(apiClient.post('/api/v1/ultraadmin/act-as/switch-role', { role })),

  clearActAs: () =>
    handleApiRequest<AuthResponse>(apiClient.post('/api/v1/ultraadmin/act-as/clear')),
}

export interface ActAsTeacher {
  id: string
  school_id: string
  last_name: string
  first_name: string
  teacher_code?: string | null
  email: string
  user_id?: string | null
  status?: string | null
  is_deleted?: boolean
}

export interface ActAsSchool {
  id: string
  name: string
  is_active: boolean
  is_deleted: boolean
}

export interface ActAsPerson {
  user_id?: string | null
  teacher_id?: string | null
  last_name: string
  first_name: string
  email: string
  is_active: boolean
  is_deleted: boolean
  roles: string[]
  teacher_code?: string | null
}

export interface ActAsPartner {
  id: string
  name: string
  is_active: boolean
  is_deleted: boolean
}

export interface ActAsUltraadmin {
  id: string
  last_name: string
  first_name: string
  email: string
  is_active: boolean
  is_deleted: boolean
}

export interface ActAsStartBody {
  role: string
  shell?: boolean
  school_id?: string | null
  user_id?: string | null
  teacher_id?: string | null
  partner_company_id?: string | null
}
